import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert,
    Button,
    Card,
    Descriptions,
    Form,
    Input,
    Popconfirm,
    Space,
    Tag,
    Typography,
    message,
} from 'antd';

import AdminUsersAPI from '../api/adminUsers';

const { Paragraph, Text } = Typography;

/**
 * Two-step 2FA reset for admins and helpdesk delegates:
 * look the user up, confirm it is the right person, then reset.
 */
export default function Reset2FA({ user }) {
    const [messageApi, contextHolder] = message.useMessage();
    const [lookupForm] = Form.useForm();
    const [target, setTarget] = useState(null);
    const [reason, setReason] = useState('');
    const [looking, setLooking] = useState(false);
    const [resetting, setResetting] = useState(false);

    const [searchParams] = useSearchParams();

    const isAdmin = !!(user && (Number(user.sys_roles) & 8));

    const onLookup = ({ username }) => {
        setLooking(true);
        setTarget(null);
        setReason('');
        AdminUsersAPI.get2faStatus(
            username.trim(),
            (res) => {
                setTarget(res);
                setLooking(false);
            },
            (err) => {
                console.log(err);
                setLooking(false);
                messageApi.error(err?.detail || 'User not found');
            }
        );
    };

    // Deep link from the admin user directory: /reset-2fa?username=<u>
    useEffect(() => {
        const username = searchParams.get('username');
        if (username) {
            lookupForm.setFieldsValue({ username });
            onLookup({ username });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const onReset = () => {
        setResetting(true);
        AdminUsersAPI.reset2fa(
            target.username,
            reason.trim(),
            (res) => {
                setResetting(false);
                messageApi.success(
                    `Reset ${target.username}: ${res.totp_deleted} TOTP device(s), ` +
                    `${res.backup_deleted} backup code(s), ` +
                    `${res.tickets_revoked} ticket(s) revoked.`
                );
                setTarget(null);
                setReason('');
                lookupForm.resetFields();
            },
            (err) => {
                console.log(err);
                setResetting(false);
                messageApi.error(err?.detail || 'Reset failed');
            }
        );
    };

    // Admins may reset with no reason; delegates must give one.
    const canSubmit = target && target.resettable
        && (isAdmin || reason.trim().length > 0);

    return <Card title="Reset 2FA">
        {contextHolder}
        <Alert
            type="warning"
            showIcon
            message="Verify identity out-of-band before resetting."
            description={
                <>
                    This clears the user's TOTP device, all backup codes, and every
                    outstanding SSO ticket. Confirm the request in person with a
                    physical ID card, or through another trusted channel.
                    {!isAdmin && ' Every reset is logged against your username and reviewed.'}
                </>
            }
            style={{ marginBottom: 16 }}
        />
        <Paragraph>
            Look up the user whose 2FA enrollment you want to clear. They will
            need to enroll again on next login.
        </Paragraph>
        <Form form={lookupForm} onFinish={onLookup} layout="inline">
            <Form.Item name="username" rules={[{ required: true }]}>
                <Input placeholder="username" allowClear />
            </Form.Item>
            <Form.Item>
                <Button type="primary" htmlType="submit" loading={looking}>
                    Look up
                </Button>
            </Form.Item>
        </Form>

        {target && <div style={{ marginTop: 24 }}>
            <Descriptions
                bordered
                size="small"
                column={1}
                title="Confirm this is the right person"
            >
                <Descriptions.Item label="Username">
                    <Text strong>{target.username}</Text>
                    {target.is_admin && <Tag color="red" style={{ marginLeft: 8 }}>Administrator</Tag>}
                    {target.is_delegate && <Tag color="blue" style={{ marginLeft: 8 }}>Helpdesk</Tag>}
                    {target.disable && <Tag style={{ marginLeft: 8 }}>Disabled</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Name">
                    {[target.firstname, target.lastname].filter(Boolean).join(' ') || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">{target.email || '—'}</Descriptions.Item>
                <Descriptions.Item label="2FA">
                    {target.totp_enrolled
                        ? <Tag color="green">Enrolled</Tag>
                        : <Tag>Not enrolled</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Backup codes left">
                    {target.backup_codes_remaining}
                </Descriptions.Item>
                <Descriptions.Item label="Active tickets">
                    {target.active_tickets} (will be revoked)
                </Descriptions.Item>
            </Descriptions>

            {!target.resettable && <Alert
                type="error"
                showIcon
                style={{ marginTop: 16 }}
                message="You cannot reset this account"
                description={target.reason}
            />}

            {target.resettable && <Space
                direction="vertical"
                size={12}
                style={{ width: '100%', marginTop: 16 }}
            >
                <Input.TextArea
                    rows={2}
                    maxLength={255}
                    showCount
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={isAdmin
                        ? 'Reason (optional) — e.g. "ID card checked at IT counter"'
                        : 'Reason (required) — how you verified their identity'}
                />
                <Popconfirm
                    title={`Reset 2FA for ${target.username}?`}
                    description="This cannot be undone. The user must enroll again."
                    okText="Reset"
                    okButtonProps={{ danger: true }}
                    onConfirm={onReset}
                    disabled={!canSubmit}
                >
                    <Button danger type="primary" disabled={!canSubmit} loading={resetting}>
                        Reset 2FA
                    </Button>
                </Popconfirm>
            </Space>}
        </div>}
    </Card>;
}
